import {
  booleanAttribute,
  Component,
  EventEmitter,
  inject,
  input,
  Input,
  numberAttribute,
  Output,
  OnInit,
  output,
  computed,
  effect,
  ViewChild,
  ElementRef,
  AfterViewInit,
  HostListener,
} from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import {
  ColDef,
  ColGroupDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ModuleRegistry,
  ClientSideRowModelModule,
  ValidationModule,
  ColumnAutoSizeModule,
  RowSelectionModule,
  CellStyleModule,
  themeMaterial,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  FilterModel,
  CustomFilterModule,
  ColumnApiModule,
} from 'ag-grid-community';
import {
  ColumnMenuModule,
  ContextMenuModule,
  MasterDetailModule,
  SetFilterModule,
} from 'ag-grid-enterprise';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ValidationModule,
  ColumnAutoSizeModule,
  RowSelectionModule,
  MasterDetailModule,
  CellStyleModule,
  ColumnMenuModule,
  ContextMenuModule,
  SetFilterModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  CustomFilterModule,
  ColumnApiModule,
]);

import {
  ColumnFilter,
  DataRequest,
  GridSort,
  PaginationState,
  paginationStateToParams,
  SortDirection,
  updatePaginationState,
} from './table.types';
import { LangService } from '../../../services/lang/lang.service';
import { SvgIconComponent } from '../../../components/svg-icon/svg-icon.component';
import { OverlayNoRowsTemplateService } from '../../services/overlay-no-rows-template/overlay-no-rows-template.service';
import { DropdownFilterComponent } from '../filters/dropdown-filter/dropdown-filter.component';
import { combineLatestWith, merge, Subject, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-table',
  imports: [AgGridAngular, SvgIconComponent],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
})
export class TableComponent<TData, TValue> implements OnInit, AfterViewInit {
  @ViewChild('container', { read: ElementRef })
  container!: ElementRef<HTMLDivElement>;
  @Input() set gridOptions(value: Partial<GridOptions>) {
    // If user-supplied value of gridOptions contains defaultColDef, merge it
    // deeply with defaultColDefDefaults.
    const cellStyle = value.defaultColDef?.cellStyle || {};
    const mergedCellStyle = {
      ...this.defaultColDefDefaults.cellStyle,
      ...cellStyle,
    };
    const defaultColDef = value.defaultColDef
      ? {
          ...this.defaultColDefDefaults,
          ...value.defaultColDef,
          cellStyle: mergedCellStyle,
        }
      : this.defaultColDefDefaults;
    this.mergedGridOptions = {
      ...this.defaultGridOptions,
      ...value,
      defaultColDef,
    };
  }

  rowData = input<TData[] | undefined | null>();

  private readonly defaultPaginationState: PaginationState = {
    currentPage: 1,
    recordsPerPage: 10,
    totalRecords: 0,
  };
  paginationState = input<PaginationState>(this.defaultPaginationState);
  paginationParams = computed(() =>
    paginationStateToParams(this.paginationState(), this.paginationConfig),
  );
  private recordsPerPage = computed(
    () => this.paginationState().recordsPerPage,
  );
  private pageSizeChangeEffect = () => {
    this.gridApi?.setGridOption('cacheBlockSize', this.recordsPerPage());
  };

  // Important!!!
  // This is where support for particular filter types is specified.
  private filterModelForFilter(
    columnFilterType: unknown,
    filter: string,
  ): object {
    if (columnFilterType === 'agTextColumnFilter') {
      return { filter, filterType: 'text', type: 'external' };
    }
    if (columnFilterType === DropdownFilterComponent) {
      return { filter };
    }
    return { filter };
  }

  private filterChangeEffect = () => {
    const columnFilter = this.filter();
    // We want to update the filters of the grid with the given value,
    // but gridApi may be unavailable at this point.  So instead of processing
    // the filter value, we feed it to a subject to be processed when there's
    // gridApi.
    this.columnFilterSubj.next(columnFilter);
  };

  private sortChangeEffect = () => {
    const sort = this.sort();
    this.sortSubj.next(sort);
  };

  columnDefs = input<ColDef<TData, TValue>[]>([]);
  fixedColumnDefs = computed(() =>
    this.columnDefs().map(def => ({
      ...def,
      suppressFloatingFilterButton: def.suppressFloatingFilterButton ?? true,
      suppressHeaderFilterButton: def.suppressFloatingFilterButton ?? true,
    })),
  );
  paginationEnabled = input(true, { transform: booleanAttribute });
  detailRowHeight = input(50, { transform: numberAttribute });
  private overlayNoRowsTemplateService = inject(OverlayNoRowsTemplateService);
  overlayNoRowsTemplate = input(
    this.overlayNoRowsTemplateService.getOverlayNoRowsTemplate(),
  );
  isServerSideGroupOpenByDefault = input(false, {
    transform: booleanAttribute,
  });
  isServerSideGroup = input(false, { transform: booleanAttribute });
  getServerSideGroupKey = input(undefined); // FIXME: type
  treeData = input(false, { transform: booleanAttribute });
  autoHeight = input(false, { transform: booleanAttribute });
  autoGroupColumnDef = input<ColDef>();
  domLayout = input<'normal' | 'autoHeight' | 'print'>('normal');

  // The following properties used to be inputs in other projects.  They can be
  // converted to input if this proves useful.
  rowSelection = 'multiple';
  masterDetail = true;
  suppressRowTransform = true;
  paginateChildRows = false;

  filter = input<ColumnFilter>([]);
  sort = input<GridSort>(GridSort.none);

  gridHeight = '0px';
  private readonly paginationHeight = 78;

  filtersChanged = output<ColumnFilter>();
  sortChanged = output<GridSort>();
  @Output() gridReady = new EventEmitter();
  dataRequest = output<DataRequest>();

  private CELL_LINE_HEIGHT = '20px';

  private readonly defaultColDefDefaults: ColDef = {
    // All columns are resizable by default.
    resizable: true,
    // This is the default composition of the column menu.  Edit if needed.
    // menuTabs: ['generalMenuTab', 'filterMenuTab', 'columnsMenuTab'],

    cellStyle: {
      lineHeight: this.CELL_LINE_HEIGHT,
    },
    // Ensure that at least a couple of letters are always visible
    minWidth: 70,
    suppressFloatingFilterButton: true,
    suppressHeaderFilterButton: true,
    // suppressHeaderMenuButton: true,

    // cellDataType set to false in order to be able to update filter values
    // when there's no data
    // https://github.com/ag-grid/ag-grid/issues/8431#issuecomment-2288233859
    cellDataType: false,

    // Trivial comparator, because sorting is done by the data source
    comparator: () => 0,
  };

  private readonly defaultGridOptions: GridOptions = {
    headerHeight: 30, // mock-up
    floatingFiltersHeight: 42, // Gibbon
  };

  mergedGridOptions: GridOptions = {
    defaultColDef: this.defaultColDefDefaults,
  };

  // Whether the table_double-header css class must be added.
  // The file table.scss uses this class to properly render No Rows overlay.
  isDoubleHeader = false;

  private langService = inject(LangService);
  private labels = this.langService.lang.shared.table;
  private paginationConfig = {
    createPageMessage: this.labels.createPageMessage,
    createRecordMessage: this.labels.createRecordMessage,
  };
  //e overlayNoRowsTemplateService: OverlayNoRowsTemplateService,

  gridApi?: GridApi<TData>;
  private gridApiSubj = new Subject<GridApi<TData>>();
  private columnFilterSubj = new Subject<ColumnFilter>();
  private sortSubj = new Subject<GridSort>();

  theme = themeMaterial.withParams({
    headerHeight: '40px',
    headerBackgroundColor: 'red',
    rowHeight: '40px',
    cellHorizontalPadding: '8px',
  });

  constructor() {
    effect(this.pageSizeChangeEffect);
    effect(this.filterChangeEffect);
    effect(this.sortChangeEffect);
    merge(this.onColumnFilterChanges, this.onSortChanges)
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  public screenHeight = '';

  private colDefHasChildren(colDef: ColDef | ColGroupDef): boolean {
    return colDef && 'children' in colDef;
  }

  ngOnInit() {
    // Inspect columnDefs, and if one with `children` is found, set isDoubleHeader to true.
    const columnDefs = this.columnDefs || this.mergedGridOptions.columnDefs;
    this.isDoubleHeader =
      Array.isArray(columnDefs) &&
      columnDefs.some(colDef => this.colDefHasChildren(colDef));
  }

  onGridReady(_params: GridReadyEvent<TData>) {
    this.gridApi = _params.api;
    this.gridApiSubj.next(this.gridApi);
    this.gridApi.sizeColumnsToFit();
    this.gridReady.emit(_params);
  }

  onFilterChanged() {
    const filterObj = this.gridApi?.getFilterModel();
    if (filterObj === null || typeof filterObj !== 'object') {
      return;
    }
    const filterValue = Object.entries(filterObj).flatMap(([key, value]) => {
      if (value === null || value === undefined || typeof value !== 'object') {
        return [];
      }
      if (!('filter' in value)) {
        return [];
      }
      return [
        {
          colId: key,
          filter: (value.filter || '').toString(),
        },
      ];
    });
    this.filtersChanged.emit(filterValue);
  }

  onSortChange() {
    const sortObj = this.gridApi?.getColumnState().find(s => s.sort != null);
    const sortBy = sortObj?.colId;
    const sortDirection = sortObj?.sort;
    this.sortChanged.emit(GridSort.of({ sortDirection, sortBy }));
  }

  onGridSizeChanged() {
    // Make sure the columns occupy all available place when the grid is resized.
    this.gridApi?.sizeColumnsToFit();
  }

  // FIXME: type event
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onPageSizeChange(event: any) {
    let recordsPerPage: number = event.target.value;
    if (Number.isNaN(recordsPerPage)) {
      // This should not happen
      recordsPerPage = 10;
    }
    this.dataRequest.emit(
      this.dataRequestForPaginationUpdate({ currentPage: 1, recordsPerPage }),
    );
  }

  onFirstPage() {
    this.dataRequest.emit(
      this.dataRequestForPaginationUpdate({ currentPage: 1 }),
    );
  }

  onLastPage() {
    const { totalRecords, recordsPerPage } = this.paginationState();
    if (totalRecords === null || totalRecords === undefined) {
      return;
    }
    const lastPage = Math.ceil(totalRecords / recordsPerPage);
    this.dataRequest.emit(
      this.dataRequestForPaginationUpdate({ currentPage: lastPage }),
    );
  }

  onNextPage() {
    this.dataRequest.emit(
      this.dataRequestForPaginationUpdate({
        currentPage: this.paginationState().currentPage + 1,
      }),
    );
  }

  onPreviousPage() {
    this.dataRequest.emit(
      this.dataRequestForPaginationUpdate({
        currentPage: this.paginationState().currentPage - 1,
      }),
    );
  }

  private dataRequestForPaginationUpdate(
    update: Partial<PaginationState>,
  ): DataRequest {
    const requestedPagination = updatePaginationState(
      this.paginationState(),
      update,
    );
    return {
      page: requestedPagination.currentPage,
      pageSize: requestedPagination.recordsPerPage,
    };
  }

  private onColumnFilterChanges = this.columnFilterSubj.pipe(
    combineLatestWith(this.gridApiSubj),
    tap(([columnFilter, gridApi]) => {
      if (columnFilter.length === 0) {
        gridApi.setFilterModel(null);
      } else {
        const filterModel: FilterModel = {};
        columnFilter.forEach(({ colId, filter }) => {
          const colDef = gridApi.getColumnDef(colId);
          const columnFilterType = colDef?.filter;
          if (columnFilterType) {
            const colFilterModel = this.filterModelForFilter(
              columnFilterType,
              filter,
            );
            filterModel[colId] = colFilterModel;
          }
        });
        gridApi.setFilterModel(filterModel);
      }
    }),
  );

  private onSortChanges = this.sortSubj.pipe(
    combineLatestWith(this.gridApiSubj),
    tap(([sort, gridApi]) => {
      const cols = gridApi.getAllGridColumns();
      const colIds = cols.map(col => col.getId());
      const sortBy =
        sort.sortDirection === SortDirection.none ? null : sort.sortBy;
      const sortDirs = {
        [SortDirection.none]: null,
        [SortDirection.asc]: 'asc' as const,
        [SortDirection.desc]: 'desc' as const,
      };
      const sortDir = sortDirs[sort.sortDirection];
      const colState = colIds.map(colId => ({
        colId,
        sort: colId === sortBy ? sortDir : null,
      }));
      gridApi.applyColumnState({ state: colState });
    }),
  );

  @HostListener('window:resize', [])
  private setGridHeight() {
    const height = this.container.nativeElement?.clientHeight;
    if (height) {
      const paginationHeight = this.paginationEnabled()
        ? this.paginationHeight
        : 0;
      this.gridHeight = `${height - paginationHeight}px`;
    }
  }

  ngAfterViewInit() {
    // setTimeout prevents ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => this.setGridHeight());
  }
}
